import { MinimalRuntimeConfig, RuntimeConfig } from './config';

/**
 * EnvToConfigMapper is a function taking a configuration key and partial
 * `RuntimeConfig` and updates `conf` in place with the appropriate configuration value.
 */
type EnvToConfigMapper<T extends MinimalRuntimeConfig> = (key: string, conf: Partial<RuntimeConfig<T>>) => void;

/**
 * EnvToConfigMap is a map of a specific env key to an `EnvToConfigMapper`.
 */
type EnvToConfigMap<T extends string, U extends MinimalRuntimeConfig> = Record<T, EnvToConfigMapper<U>>;

/**
 * configFromEnv creates a `RuntimeConfig` from `process.env`.
 *
 * The current environment type (server vs browser) is detected by testing for
 * the browser first. No validation occurs during configFromEnv, and an empty
 * config can be returned.
 *
 * See tests for an example.
 */
const configFromEnv = <T extends string, U extends MinimalRuntimeConfig>(
  mapper: EnvToConfigMap<T, U>
): Partial<RuntimeConfig<U>> => {
  const conf: Partial<RuntimeConfig<U>> = {};
  const keyPrefix = Object.keys(process.env).some((v) => v.startsWith('REACT_APP_')) ? 'REACT_APP_' : '';

  // There's not a super clean way to iterate through the enum key/values here.
  // The EnvToConfigMapper object is used to avoid dupe references to named env
  // vars.
  Object.keys(mapper).forEach((k) => {
    const envKey = k as keyof typeof mapper;
    const envVal = process.env[`${keyPrefix}${k}`];

    if (envVal !== undefined) {
      mapper[envKey](envVal, conf);
    }
  });

  return conf;
};

export type { EnvToConfigMapper, EnvToConfigMap };
export { configFromEnv };
